import { Component, Element, State, h } from '@stencil/core';

import TyckaClient from '../../../../../tycka-sdk/src/TyckaClient';

import { Color, TyckaConfig } from '../../../utils/types';
import { extractElementAttributes } from '../../../utils/dom';

@Component({
  tag: 'tycka-rating-stars',
  styleUrl: 'rating-stars.css',
  shadow: true,
})
export class StarsWidget {
  private config: TyckaConfig;
  private tyckaClient: TyckaClient;

  @Element() hostElement: HTMLElement;

  @State() filterAttributes: { [key: string]: string } = null;
  @State() averageRating: number;
  @State() numberOfReviews: number;
  @State() configColor: Color;
  @State() translations: { [key: string]: string } = null;

  @State() requestPending: boolean = false;
  @State() showStarsCompact: boolean = false;

  async componentWillLoad() {
    this.filterAttributes = extractElementAttributes(this.hostElement);
    await this.load();

    if (typeof window !== 'undefined') {
      window.addEventListener('tycka-widget-config-updated', async _ => {
        await this.load();
      });
    }
  }

  load = async () => {
    if (typeof window === 'undefined' || !(window as any).__TYCKA_CONFIG) {
      return;
    }

    this.config = (
      window as Window &
        typeof globalThis & {
          __TYCKA_CONFIG: TyckaConfig;
        }
    ).__TYCKA_CONFIG;

    if (this.config.preview) {
      this.averageRating = 3;
      this.numberOfReviews = 150;
    } else {
      this.fetchAverageRating();
    }
    this.configColor = this.config.widgets?.all?.color;
    this.translations = this.config.widgets?.rating?.translations;
    this.showStarsCompact = this.config.widgets?.rating?.setting?.stars;
  };

  fetchAverageRating = async () => {
    this.tyckaClient = new TyckaClient(this.config);
    this.requestPending = true;

    try {
      const response = await this.tyckaClient.fetchAverageRating(this.filterAttributes, true);
      const responseKey = Object.keys(response)[0];
      this.averageRating = response[responseKey]?.[0]?.average_rating || 0;
      this.numberOfReviews = response[responseKey]?.[0]?.review_count || 0;

      window.dispatchEvent(new CustomEvent('tycka-average-rating-fetched', {
        detail: {
          averageRating: this.averageRating,
          numberOfReviews: this.numberOfReviews,
          filterAttributes: this.filterAttributes,
        }
      }));
    } catch (error) {
      console.error(error);
    }
    this.requestPending = false;
  };

  scrollToReviews = () => {
    const element = document.querySelector('review-list');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  render() {
    return !this.requestPending && this.numberOfReviews > 0 && this.configColor ? (
      <div
        class="rating-stars" 
        part="globalbackgroundcolor headingandlabeltextcolor rating-stars"
        onClick={() => this.scrollToReviews()}
        style={{backgroundColor: this.configColor.background, color: this.configColor.headingandlabel}}
      >
        {!this.showStarsCompact
          ? <tycka-stars-component 
            rating={this.averageRating}
            fillColor={this.configColor.stars}
            emptyColor={this.configColor.rating}
            exportparts='variableratingspecialcolor, variablestarsspecialcolor, star'
          />
          : <tycka-star-component
            exportparts='variableratingspecialcolor, variablestarsspecialcolor, star'
            fillColor={this.configColor.stars}
            emptyColor={this.configColor.rating}
            filledStar={5}
          />}

        {this.showStarsCompact &&
          <span part='rating-stars-count rating-stars-average' class="rating-stars-count">
            {this.averageRating.toFixed(1)}
          </span>}
        
        <span part='rating-stars-count' class="rating-stars-count">
          ({this.numberOfReviews}) {this.numberOfReviews === 1
            ? this.translations.singlereview
            : this.translations.multiplereviews}
        </span>
      </div>
    ) : null;
  }
}
